import * as React from 'react';
import moment from 'moment';
import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import {
  OSC_BD_READ_MARGIN_MS,
  buildOscBdComparisonData,
  findOscBdMeasurementByID,
  formatOscBdUnavailableText,
  isOscBdAcceptable,
  isOscBdPostMeasurement,
  normalizeOscBdMeasurementID,
  resolveOscBdDisplaySettings,
  resolveOscBdRole,
  resolveOscBdUnavailableReason,
} from '../../../libs/oscBdLib';
import {
  bdAdministeredMoment,
  fmtBdWaitOverride,
  resolveBdAdministration,
} from '../../../libs/bdLib';
import { getMeasurementsForPatient, getParticipantMeasurements } from '../../../libs/trialsLib';
import type { PatientID } from '../../../Types/Common/src/ids';
import type { OscillometryMeasurement, VersionlessRangeKey } from '../../../Types/Common/src/measurement';
import type { Program } from '../../../Types/Common/src/program';

import './OscBdComparisonPanel.scss';

const { useEffect, useMemo, useState } = React;

type ComparisonStatus = 'loading' | 'resolved' | 'unavailable' | 'error';

interface ComparisonState {
  requestKey: string;
  status: ComparisonStatus;
  companion?: OscillometryMeasurement;
  /** Latest OSCAVG rows on the viewed kiosk, used to explain an unavailable table. */
  kioskAverages?: OscillometryMeasurement[];
}

interface OscBdComparisonPanelProps {
  patientID: PatientID;
  viewedMeasurement: OscillometryMeasurement;
  featureSet?: Program['featureSet'];
  translations?: Record<string, string | undefined>;
}

const verdictClassName = (verdict: boolean | undefined): string =>
  verdict === true
    ? 'osc-bd-panel__verdict osc-bd-panel__verdict--met'
    : 'osc-bd-panel__verdict';

const directionalClassName = (
  value: number | undefined,
  criterionMet: boolean | undefined,
): string => {
  const baseClassName = 'osc-bd-panel__directional';
  if (value === undefined || value === 0) return baseClassName;
  if (value < 0) return `${baseClassName} ${baseClassName}--declined`;
  return criterionMet
    ? `${baseClassName} ${baseClassName}--met`
    : `${baseClassName} ${baseClassName}--improved`;
};

const formatThreshold = (threshold: number): string =>
  `${(threshold * 100).toFixed(2)}%`;

export const OscBdComparisonPanel: React.FC<OscBdComparisonPanelProps> = ({
  patientID,
  viewedMeasurement,
  featureSet,
  translations = {},
}) => {
  const settings = useMemo(
    () => resolveOscBdDisplaySettings(featureSet),
    [featureSet],
  );
  const labelConfig = useMemo(
    () => ({
      preBdLabels: featureSet?.config_PreBdLabels,
      postBdLabels: featureSet?.config_PostBdLabels,
    }),
    [featureSet?.config_PreBdLabels, featureSet?.config_PostBdLabels],
  );
  const viewedMeasurementID = normalizeOscBdMeasurementID(viewedMeasurement.measurementID);
  const companionMeasurementID = normalizeOscBdMeasurementID(viewedMeasurement.linkedBDMeasurementID);
  const isLinked = !!viewedMeasurementID
    && !!companionMeasurementID
    && viewedMeasurementID !== companionMeasurementID;
  const mode = !settings.enabled || !viewedMeasurementID
    ? undefined
    : isLinked
      ? 'linked'
      : resolveOscBdRole(viewedMeasurement, labelConfig) ? 'unlinked' : undefined;
  const requestKey = mode
    ? `${mode}|${patientID}|${viewedMeasurement.measurementID}|${companionMeasurementID ?? ''}`
    : undefined;
  const kioskID = viewedMeasurement.kioskID;
  const anchorMs = isOscBdPostMeasurement(viewedMeasurement)
    ? viewedMeasurement.bdAdministeredAt!
    : Date.parse(viewedMeasurement.roundtripTime);
  // The viewed series' own quality explains the gap without reading the kiosk.
  const needsKioskAverages = isOscBdAcceptable(viewedMeasurement) && !!kioskID && Number.isFinite(anchorMs);
  const [state, setState] = useState<ComparisonState | undefined>();

  useEffect(() => {
    if (!requestKey) {
      setState(undefined);
      return;
    }

    let cancelled = false;

    const showUnavailable = async () => {
      setState({ requestKey, status: 'unavailable' });
      if (!needsKioskAverages || !kioskID) return;
      try {
        const response = await getMeasurementsForPatient(
          patientID,
          'OSCAVG',
          kioskID,
          new Date(anchorMs - OSC_BD_READ_MARGIN_MS).toISOString(),
        ) as OscillometryMeasurement[];
        if (cancelled || !Array.isArray(response)) return;
        setState({
          requestKey,
          status: 'unavailable',
          kioskAverages: response.filter(measurement => measurement.type === 'OSCAVG'),
        });
      } catch {
        // Only the generic notice is shown when the kiosk rows cannot be read.
      }
    };

    if (mode === 'unlinked' || !companionMeasurementID) {
      void showUnavailable();
      return () => {
        cancelled = true;
      };
    }

    setState({ requestKey, status: 'loading' });

    (async () => {
      try {
        const response = await getParticipantMeasurements(
          patientID,
          companionMeasurementID as VersionlessRangeKey<'OSCAVG'>,
          true,
        ) as OscillometryMeasurement[];
        if (cancelled) return;

        const companion = findOscBdMeasurementByID(
          Array.isArray(response)
            ? response.filter(measurement => measurement.type === 'OSCAVG')
            : [],
          companionMeasurementID,
        );
        if (!companion) {
          setState({ requestKey, status: 'error' });
          return;
        }
        // A companion without a link is awaiting repair; one linked elsewhere was re-paired.
        const companionLink = normalizeOscBdMeasurementID(companion.linkedBDMeasurementID);
        if (companionLink && companionLink !== viewedMeasurementID) {
          await showUnavailable();
          return;
        }
        setState({ requestKey, status: 'resolved', companion });
      } catch {
        if (!cancelled) setState({ requestKey, status: 'error' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    anchorMs,
    companionMeasurementID,
    kioskID,
    mode,
    needsKioskAverages,
    patientID,
    requestKey,
    viewedMeasurementID,
  ]);

  const currentState: ComparisonState | undefined = state?.requestKey === requestKey
    ? state
    : requestKey
      ? { requestKey, status: mode === 'unlinked' ? 'unavailable' as const : 'loading' as const }
      : undefined;
  const unavailableReason = useMemo(
    () => currentState?.status === 'unavailable' && (!needsKioskAverages || currentState.kioskAverages)
      ? resolveOscBdUnavailableReason(viewedMeasurement, currentState.kioskAverages ?? [], labelConfig)
      : undefined,
    [currentState?.status, currentState?.kioskAverages, labelConfig, needsKioskAverages, viewedMeasurement],
  );
  const comparisonData = currentState?.status === 'resolved' && currentState.companion
    ? buildOscBdComparisonData(
      viewedMeasurement,
      currentState.companion,
      settings.thresholds,
      settings.metrics,
    )
    : undefined;

  if (!requestKey || !currentState) return null;

  const labels = {
    title: translations.bronchodilatorResponse || 'Bronchodilator response',
    metric: translations.metric || 'Metric',
    pre: translations.preBd || 'Pre-BD',
    post: translations.postBd || 'Post-BD',
    change: translations.change || 'Change',
    absolute: translations.absolute || 'Absolute',
    improvement: translations.improvementFromBaseline || 'Improvement from baseline',
    criterion: translations.responseCriterion
      || translations.criterion
      || translations.criterionMet
      || 'Criterion',
    overall: translations.overallReversibility || 'Overall reversibility',
    met: translations.met || 'Met',
    notMet: translations.notMet || 'Not met',
    reversible: translations.reversible || 'Reversible',
    notReversible: translations.notReversible || 'Not reversible',
    increase: translations.increase || 'Increase',
    decrease: translations.decrease || 'Decrease',
  };
  const companionPath = companionMeasurementID && currentState.status !== 'unavailable'
    ? `/participant/${patientID}/osc/${companionMeasurementID}`
    : undefined;
  const unavailableText = formatOscBdUnavailableText(unavailableReason, translations);
  const superseded = unavailableReason?.kind === 'superseded' ? unavailableReason : undefined;
  const companionRole = comparisonData
    ? comparisonData.pre.measurementID === currentState.companion?.measurementID
      ? labels.pre
      : labels.post
    : undefined;
  const companionTimestamp = currentState.companion
    ? moment.parseZone(currentState.companion.roundtripTime).format('MMM D, h:mm A')
    : undefined;
  const postMeasurement = comparisonData && currentState.companion
    ? comparisonData.post.measurementID === viewedMeasurement.measurementID
      ? viewedMeasurement
      : currentState.companion
    : undefined;
  const administration = resolveBdAdministration(postMeasurement);
  const overrideNote = fmtBdWaitOverride(administration?.waitOverride);
  const { showAbsolute, showPercent } = settings.columns;
  const changeColumnCount = Number(showAbsolute) + Number(showPercent);
  const leadingColumnCount = 3 + changeColumnCount;
  const tableColumnCount = leadingColumnCount + Number(settings.showVerdict);
  const tableMinWidth = Math.max(520, tableColumnCount * 140);
  const formatMetricVerdict = (
    verdict: boolean | undefined,
    unavailableValue: string,
  ): string => verdict === undefined ? unavailableValue : verdict ? labels.met : labels.notMet;
  const formatOverallVerdict = (
    verdict: boolean | undefined,
    unavailableValue: string,
  ): string => verdict === undefined
    ? unavailableValue
    : verdict
      ? labels.reversible
      : labels.notReversible;

  return (
    <section className="osc-bd-panel" aria-labelledby="osc-bd-panel-title">
      <div className="osc-bd-panel__heading">
        <div>
          <h3 id="osc-bd-panel-title">{labels.title}</h3>
          {companionRole && (
            <span className="osc-bd-panel__context">
              Compared with {companionRole} measurement
              {companionTimestamp && ` · ${companionTimestamp}`}
            </span>
          )}
        </div>
        {companionPath && (
          <Link
            className="osc-bd-panel__link"
            to={companionPath}
            target="_blank"
            rel="noopener noreferrer"
          >
            View companion measurement <span aria-hidden="true">→</span>
          </Link>
        )}
      </div>

      {administration && postMeasurement && (
        <div className="osc-bd-panel__administration">
          Bronchodilator administered {bdAdministeredMoment(
            administration.administeredAtMs,
            postMeasurement.roundtripTime,
          ).format('MMM D, h:mm A')}
          {administration.dose ? ` · Dose: ${administration.dose}` : ''}
          {overrideNote && (
            <span className="osc-bd-panel__override"> · {overrideNote}</span>
          )}
        </div>
      )}

      {currentState.status === 'loading' && (
        <div
          className="osc-bd-panel__status"
          role="status"
          aria-live="polite"
          aria-label={translations.loadingBronchodilatorComparison || 'Loading bronchodilator comparison'}
        >
          <Spinner animation="border" size="sm" aria-hidden="true" />
          <span>{translations.loading || 'Loading companion measurement…'}</span>
        </div>
      )}

      {currentState.status === 'error' && (
        <div className="osc-bd-panel__status osc-bd-panel__status--error" role="alert">
          {translations.failedToLoadBronchodilatorComparison
            || 'The companion measurement could not be loaded.'}
        </div>
      )}

      {currentState.status === 'unavailable' && (
        <div
          className="osc-bd-panel__status osc-bd-panel__status--unavailable"
          role="status"
          aria-live="polite"
        >
          <span className="osc-bd-panel__unavailable-summary">{unavailableText.summary}</span>
          {unavailableText.detail && (
            <span className="osc-bd-panel__unavailable-detail">
              {unavailableText.detail}
              {superseded && (
                <>
                  {' '}
                  <Link
                    className="osc-bd-panel__link"
                    to={`/participant/${patientID}/osc/${superseded.measurementID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {superseded.role === 'pre' ? labels.pre : labels.post} measurement
                    {' · '}{moment.parseZone(superseded.roundtripTime).format('MMM D, h:mm A')}
                    {' '}<span aria-hidden="true">→</span>
                  </Link>
                </>
              )}
            </span>
          )}
        </div>
      )}

      {comparisonData && (
        <div
          className="osc-bd-panel__scroll"
          role="region"
          aria-label={
            translations.scrollableBronchodilatorComparison
            || 'Scrollable bronchodilator comparison'
          }
          tabIndex={0}
        >
          <table
            aria-label={
              translations.oscBdComparison
              || 'Pre- and post-bronchodilator oscillometry comparison'
            }
            style={{ minWidth: `${tableMinWidth}px` }}
          >
            <colgroup>
              <col className="osc-bd-panel__col-metric" />
              <col className="osc-bd-panel__col-pre" />
              <col className="osc-bd-panel__col-post" />
              {showAbsolute && <col className="osc-bd-panel__col-absolute" />}
              {showPercent && <col className="osc-bd-panel__col-improvement" />}
              {settings.showVerdict && <col className="osc-bd-panel__col-criterion" />}
            </colgroup>
            <thead>
              <tr>
                <th scope="col" rowSpan={2}>{labels.metric}</th>
                <th className="osc-bd-panel__group-start" scope="col" rowSpan={2}>{labels.pre}</th>
                <th className="osc-bd-panel__group-start" scope="col" rowSpan={2}>{labels.post}</th>
                <th
                  className="osc-bd-panel__group-start"
                  scope="colgroup"
                  colSpan={changeColumnCount}
                >
                  {labels.change}
                </th>
                {settings.showVerdict && (
                  <th
                    className="osc-bd-panel__group-start"
                    scope="col"
                    rowSpan={2}
                  >
                    {labels.criterion}
                  </th>
                )}
              </tr>
              <tr>
                {showAbsolute && <th className="osc-bd-panel__group-start" scope="col">{labels.absolute}</th>}
                {showPercent && (
                  <th
                    className={showAbsolute ? 'osc-bd-panel__change-subcolumn' : 'osc-bd-panel__group-start'}
                    scope="col"
                  >
                    {labels.improvement}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {comparisonData.rows.map(row => (
                <tr key={row.metric}>
                  <th scope="row">
                    <span className="osc-bd-panel__metric-name">
                      {row.label}
                      {row.unit && <span className="osc-bd-panel__unit"> ({row.unit})</span>}
                    </span>
                    <span className="osc-bd-panel__criterion">
                      {row.direction === 'increase' ? labels.increase : labels.decrease}
                      {' ≥ '}{formatThreshold(row.threshold)}
                    </span>
                  </th>
                  <td className="osc-bd-panel__group-start">{row.formatted.pre}</td>
                  <td className="osc-bd-panel__group-start">{row.formatted.post}</td>
                  {showAbsolute && <td className="osc-bd-panel__group-start">{row.formatted.absoluteChange}</td>}
                  {showPercent && (
                    <td className={showAbsolute ? 'osc-bd-panel__change-subcolumn' : 'osc-bd-panel__group-start'}>
                      <span className={directionalClassName(
                        row.pctFromBaseline,
                        settings.showVerdict ? row.reversibilityMet : undefined,
                      )}>
                        {row.pctFromBaseline !== undefined && row.pctFromBaseline !== 0 && (
                          <span className="osc-bd-panel__directional-arrow" aria-hidden="true">
                            {row.pctFromBaseline > 0 ? '▲' : '▼'}
                          </span>
                        )}
                        {row.formatted.pctFromBaseline}
                      </span>
                    </td>
                  )}
                  {settings.showVerdict && (
                    <td className="osc-bd-panel__group-start">
                      <span className={verdictClassName(row.reversibilityMet)}>
                        {formatMetricVerdict(
                          row.reversibilityMet,
                          row.formatted.reversibilityMet,
                        )}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {settings.showVerdict && (
              <tfoot>
                <tr className={comparisonData.overallReversibility ? 'osc-bd-panel__overall--met' : undefined}>
                  <th colSpan={leadingColumnCount} scope="row">{labels.overall}</th>
                  <td className="osc-bd-panel__group-start">
                    <strong className={verdictClassName(comparisonData.overallReversibility)}>
                      {formatOverallVerdict(
                        comparisonData.overallReversibility,
                        comparisonData.formattedOverallReversibility,
                      )}
                    </strong>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </section>
  );
};
